<template>
  <svg
    ref="viewport"
    class="viewport"
    xmlns="http://www.w3.org/2000/svg"
    @mousedown="handleMouseDown"
    @mouseup="handleMouseUpAndLeave"
    @mouseleave="handleMouseUpAndLeave"
    @touchstart="handleMouseDown"
    @touchend="handleMouseUpAndLeave"
    @touchcancel="handleMouseUpAndLeave"
    @contextmenu.prevent
  >
    <defs>
      <pattern
        id="diagonalHatch"
        height="10"
        patternUnits="userSpaceOnUse"
        width="10"
      >
        <rect fill="#5594e7" height="10" width="10"/>
        <path d="M-1,1 l2,-2 M0,10 l10,-10 M9,11 l2,-2"/>
      </pattern>
    </defs>
    <g id="viewport-content" ref="viewportContent">
      <template v-if="!sunstudyMode">
        <image
          :style="{opacity: isCenteredAndLoaded? 1 : 0}"
          class="unit-view"
          :height="exterior.height"
          :href="mainImage"
          :width="exterior.width"
          x="0"
          y="0"
        />
      </template>
      <template v-else v-for="(img, idx) in sunstudy.images">
        <transition name="slide-fade">
          <image
            v-show="idx === sunstudyPosition"
            class="unit-view"
            :height="exterior.height"
            :href="img.url"
            :width="exterior.width"
            x="0"
            y="0"
          />
        </transition>
      </template>

      <apartment-overlay
        v-for="overlay in reorderedOverlays"
        :key="overlay.overlay_id"
        ref="overlay"
        :selected-overlay="selectedOverlay"
        :overlay="overlay"
        :overlay-status="isApartmentOverlayShown"
        :overlay-transition="overlayTransition"
        :unitById="unitById"
        :units="units"
        :unitTypeById="unitTypeById"
        :statuses="statuses"
        :attributes="attributes"
        :i18nTranslations="i18nTranslations"
        :showExternalFloorPlan="showExternalFloorPlan"
        :floorPlanOnTooltipShowing="floorPlanOnTooltipShowing"
        :floorPlan2DFirstShowing="floorPlan2DFirstShowing"
        :exteriors="exteriors"
        @toggleOverlay="$emit('toggleOverlay', $event)"
      />
    </g>
  </svg>
</template>

<script>
import { mapGetters } from 'vuex';
import { cdnUrl } from '@/utils/urls';
import ApartmentOverlay from './ApartmentOverlay.vue';

export default {
  name: 'Viewport',
  components: {
    ApartmentOverlay,
  },
  props: {
    exterior: {
      type: Object,
      required: true,
    },
    overlays: {
      type: Array,
      default: () => [],
    },
    selectedOverlay: {
      type: [Number, undefined],
      default: 0,
    },
    overlayShowingStatus: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapGetters('filters', ['filterCheckers', 'filtersSorted', 'activeFilters', 'unitList']),
    ...mapGetters('service', ['exteriorNavBarVisible']),
    ...mapGetters('sunstudy', ['sunstudyAvailable', 'sunstudyMode', 'sunstudy', 'sunstudyPosition']),
    ...mapGetters('units', ['unitById', 'units']),
    ...mapGetters('unitTypes', ['unitTypeById']),
    ...mapGetters(
      'project',
      [
        'statuses',
        'attributes',
        'i18nTranslations',
        'showExternalFloorPlan',
        'floorPlanOnTooltipShowing',
        'floorPlan2DFirstShowing',
      ],
    ),
    ...mapGetters('exteriors', ['exteriors']),
    reorderedOverlays() {
      // the reordering is needed to fix showing of the selected overlay's borders
      // which can be overlapped by other overlays.
      // So to fix it the selected overlay is moved at the end of the list to be drawn the last.
      const pos = this.overlays.findIndex(overlay => overlay.overlay_id === this.selectedOverlay);

      return pos === -1
        ? this.overlays
        : this.overlays.slice(0, pos)
          .concat(this.overlays.slice(pos + 1), this.overlays[pos]);
    },
    mainImage() {
      return cdnUrl(this.exterior.image);
    },
  },
  data() {
    return {
      isCenteredAndLoaded: false,
      isCentered: false,
      holdTimeout: null,
      isApartmentOverlayShown: this.overlayShowingStatus,
      overlayTransition: false,
    }
  },
  watch: {
    selectedOverlay() {
      this.centerViewport();
    },
    overlayShowingStatus(newVal) {
      this.isApartmentOverlayShown = newVal
    }
  },
  mounted() {
    window.addEventListener('resize',  this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    handleMouseDown(event) {
      if (event.button === 0 || event.button === 2) {
        this.holdTimeout = setTimeout(() => {
          this.onHold();
        }, 0);
      }
    },
    handleMouseUpAndLeave() {
      clearTimeout(this.holdTimeout);
      this.isApartmentOverlayShown = this.overlayShowingStatus
      this.overlayTransition = false
    },
    onHold() {
      this.isApartmentOverlayShown = true
      this.overlayTransition = true
    },
    handleResize() {
      // this listener prevents centralizing 2 times and centralize only on resize
      this.isCentered = false
    },
    centerViewport() {
      const overlays = this.$refs.overlay && this.selectedOverlay
        // centering by selected overlay only
        ? this.$refs.overlay.filter(({ overlay, clicked }) => !clicked && overlay.overlay_id === this.selectedOverlay)
        // centering by all overlays
        : this.$refs.overlay;
      this.isCentered = false
      this.centerViewportByOverlays(overlays);
    },
    centerViewportByOverlays(overlays = []) {
      if (!overlays.length) {
        const { width, height } = this.$refs.viewportContent.getBoundingClientRect();
        const root = document.documentElement;
        const toLeft = (width-root.offsetWidth)/2;
        const toBottom =  (height-root.offsetHeight)/2 + 56;
        const scroller = this.$el.parentNode
        scroller.scrollTo(toLeft, toBottom);
        window.scroll(0, toBottom);
        this.$nextTick(() => {
          this.isCenteredAndLoaded = true;
          this.$emit('loadedMainImg');
        });
        return;
      }

      const overlayDimensions = {
        top: [],
        left: [],
        right: [],
        bottom: [],
      };
      const viewportBBox = this.$refs.viewportContent.getBoundingClientRect();

      overlays.forEach((overlay) => {
        if (!overlay.$el) {
          return;
        }

        const boundingRect = overlay.$el.getBoundingClientRect();

        overlayDimensions.top.push(boundingRect.top - viewportBBox.top);
        overlayDimensions.left.push(boundingRect.left - viewportBBox.left);
        overlayDimensions.right.push(boundingRect.right - viewportBBox.left);
        overlayDimensions.bottom.push(boundingRect.bottom - viewportBBox.top);
      });

      const minTop = Math.min(...overlayDimensions.top);
      const minLeft = Math.min(...overlayDimensions.left);
      const maxRight = Math.max(...overlayDimensions.right);
      const maxBottom = Math.max(...overlayDimensions.bottom);

      const root = document.documentElement;
      // const root = this.$el.parentElement;

      if (maxRight > 0 && maxBottom > 0) {
        let leftScroll;
        let topScroll;

        if (root.offsetWidth < (maxRight - minLeft)) {
          // img doesnt fit
          const overlayContentWidth = maxRight - minLeft
          const narrowViewPortDelta = (overlayContentWidth - root.offsetWidth) / 2;
          leftScroll = minLeft + narrowViewPortDelta;
        } else {
          // img fits
          leftScroll = ((maxRight + minLeft) - root.offsetWidth) / 2;
        }

        const height = root.offsetHeight - 56 - (this.exteriorNavBarVisible ? 71 : 0);

        if (height < (maxBottom - minTop)) {
          topScroll = minTop;
        } else {
          topScroll = ((maxBottom + minTop) - height) / 2;
        }

        const scroller = this.$el.parentNode

        scroller.scrollTo(
          leftScroll,
          topScroll,
        );
        window.scroll(0, topScroll);

        root.scrollLeft = !isFinite(maxRight) || !isFinite(minLeft)
          ? (this.$el.scrollWidth - root.offsetWidth) / 2
          : leftScroll;
      }
      this.$nextTick(() => {
        this.isCenteredAndLoaded = true;
        this.$emit('loadedMainImg');
      });
      this.isCentered = true
    },
  },
};
</script>
<style scoped>
.slide-fade-enter-active {
  animation: fadeEffect 0.8s;
}

.slide-fade-leave-active {
  animation: fadeEffect 1s reverse;
}
@keyframes fadeEffect {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
